import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, Observable, switchMap, take } from 'rxjs';
import { LoggedUserService } from './logged-user.service';

/**
 * Centralized read access to the selected tenant's feature flags.
 *
 * The effective feature map is resolved server-side (defaults ⊕ tenant overrides)
 * and delivered in the tenant payload, so this service only reads it. It is the
 * single source of truth for feature checks on the client: used by the
 * `*appHasFeature` directive, the `featureGuard` route guard and the sidebar.
 *
 * Default semantics: a feature is considered enabled unless its flag is explicitly
 * `false`. So a missing flag (e.g. before the tenant has loaded) never hides an
 * existing module — consistent with the non-breaking, default-on backend behavior.
 */
@Injectable({ providedIn: 'root' })
export class TenantConfigService {
  private readonly _loggedUserService = inject(LoggedUserService);

  /** Resolved feature map of the currently selected tenant. */
  private readonly _features: Signal<Record<string, boolean>> = toSignal(
    this._loggedUserService.selectedTenant$.pipe(
      map((tenant) => tenant?.settings?.features ?? {}),
    ),
    { initialValue: {} },
  );

  /**
   * Whether a feature is enabled for the current tenant (reactive, signal-based).
   * For use inside `computed`/`effect` (directive, sidebar).
   */
  hasFeature(feature: string): boolean {
    return this._features()[feature] !== false;
  }

  /**
   * Observable variant that waits for the tenant to be loaded before resolving —
   * for route guards (CanMatch), where the tenant is not yet available synchronously.
   *
   * Subscribes to `tenants$` (the active loader) first, so a direct navigation or a
   * page refresh onto a gated route triggers the tenant load. `selectedTenant$` alone
   * is passive — it derives from the tenants subject which is only populated once
   * `tenants$` is subscribed (by MainLayout, during activation). Since CanMatch runs
   * during route recognition (before MainLayout mounts), waiting on `selectedTenant$`
   * directly would deadlock and leave a blank page on refresh.
   */
  hasFeature$(feature: string): Observable<boolean> {
    return this._loggedUserService.tenants$.pipe(
      switchMap(() => this._loggedUserService.selectedTenant$),
      filter((tenant) => tenant !== null),
      take(1),
      map((tenant) => tenant?.settings?.features?.[feature] !== false),
    );
  }
}
